import React from "react";
import { motion } from "framer-motion";
import {
  Upload, CheckCircle2, XCircle, Clock, FileText, Trash2,
  AlertCircle, Image as ImageIcon, Loader2,
} from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

const ACCEPTED_INPUT = "image/jpeg,image/png,image/heic,image/heif,image/webp,application/pdf";
const STATUS_META = {
  pending: {
    label: "Awaiting review",
    Icon: Clock,
    classes: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40",
  },
  approved: {
    label: "Approved",
    Icon: CheckCircle2,
    classes: "bg-[#5BB247]/10 text-[#2c8642] border-[#5BB247]/40",
  },
  rejected: {
    label: "Rejected",
    Icon: XCircle,
    classes: "bg-[#B91C1C]/10 text-[#B91C1C] border-[#FCA5A5]",
  },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const I = meta.Icon;
  return (
    <span
      data-testid={`doc-status-${status}`}
      className={`inline-flex items-center gap-1.5 border px-2 py-1 text-[11px] uppercase tracking-[0.14em] font-bold ${meta.classes}`}
    >
      <I size={12} />
      {meta.label}
    </span>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DriverDocuments({ onChange }) {
  const [catalogue, setCatalogue] = React.useState({ required: [], optional: [] });
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openType, setOpenType] = React.useState(null);
  const [formError, setFormError] = React.useState("");

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const [cat, list] = await Promise.all([
        apiClient.get("/driver/documents/catalogue"),
        apiClient.get("/driver/documents"),
      ]);
      setCatalogue(cat.data);
      setDocs(list.data.documents || []);
    } catch (e) {
      setFormError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load documents.");
    } finally {
      setLoading(false);
    }
    if (onChange) onChange();
  }, [onChange]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const docByType = React.useMemo(() => {
    const map = {};
    docs.forEach((d) => { map[d.doc_type] = d; });
    return map;
  }, [docs]);

  const handleDelete = async (docId) => {
    try {
      await apiClient.delete(`/driver/documents/${docId}`);
      await refresh();
    } catch (e) {
      setFormError(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div data-testid="driver-docs-loading" className="text-[#475569] text-sm inline-flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading documents…
      </div>
    );
  }

  const renderItem = (item, isOptional) => {
    const existing = docByType[item.key];
    return (
      <motion.div
        key={item.key}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        data-testid={`doc-card-${item.key}`}
        className="bg-white border border-[#0A0A0A]/10 p-5 flex flex-col"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-display font-bold text-[#0A0A0A] tracking-tight leading-tight">
              {item.label}
            </p>
            {item.unlocks && (
              <p className="text-[11px] mt-1 text-[#475569]">
                Unlocks: {item.unlocks}
              </p>
            )}
            {isOptional && (
              <p className="text-[11px] mt-1 text-[#475569]">Optional</p>
            )}
          </div>
          {existing && <StatusPill status={existing.status} />}
        </div>

        {existing ? (
          <div className="mt-4 text-sm text-[#475569] space-y-1.5">
            <p className="flex items-center gap-2">
              <FileText size={14} className="text-[#1E4D55]" />
              <span className="truncate">{existing.file_name}</span>
              <span className="text-[11px]">· {formatBytes(existing.file_size)}</span>
            </p>
            {existing.document_number && (
              <p className="text-xs">
                <span className="uppercase tracking-wider font-bold text-[#0A0A0A] mr-2">Number</span>
                {existing.document_number}
              </p>
            )}
            {(existing.valid_from || existing.valid_until) && (
              <p className="text-xs">
                <span className="uppercase tracking-wider font-bold text-[#0A0A0A] mr-2">Valid</span>
                {existing.valid_from || "—"} → {existing.valid_until || "—"}
              </p>
            )}
            {existing.admin_notes && (
              <p className={`text-xs p-2 mt-2 border ${
                existing.status === "rejected"
                  ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#B91C1C]"
                  : "bg-[#F5F5F5] border-[#0A0A0A]/10 text-[#0A0A0A]"
              }`}>
                <span className="font-bold uppercase tracking-wider mr-1">Admin note</span>
                {existing.admin_notes}
              </p>
            )}
          </div>
        ) : (
          <p className="mt-4 text-sm text-[#475569]">Not uploaded yet.</p>
        )}

        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpenType(item.key)}
            data-testid={`doc-upload-button-${item.key}`}
            className="inline-flex items-center gap-1.5 bg-[#1E4D55] text-white hover:bg-[#163C42] transition-colors px-3 py-2 text-xs font-semibold"
          >
            <Upload size={12} />
            {existing ? "Replace" : "Upload"}
          </button>
          {existing && (
            <button
              type="button"
              onClick={() => handleDelete(existing.id)}
              data-testid={`doc-delete-button-${item.key}`}
              className="inline-flex items-center gap-1.5 border border-[#0A0A0A]/15 hover:border-[#B91C1C] hover:text-[#B91C1C] transition-colors px-3 py-2 text-xs font-semibold text-[#475569]"
            >
              <Trash2 size={12} />
              Remove
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div data-testid="driver-documents-section">
      {formError && (
        <div className="mb-4 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{formError}</span>
        </div>
      )}

      <h3 className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55] mt-2 mb-4">
        Required documents — all must be approved
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {catalogue.required.map((item) => renderItem(item, false))}
      </div>

      <h3 className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55] mt-12 mb-4">
        Optional — unlock more job types
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {catalogue.optional.map((item) => renderItem(item, true))}
      </div>

      {openType && (
        <UploadModal
          docType={catalogue.required.concat(catalogue.optional).find((d) => d.key === openType)}
          existing={docByType[openType]}
          onClose={() => setOpenType(null)}
          onUploaded={async () => {
            setOpenType(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function UploadModal({ docType, existing, onClose, onUploaded }) {
  const [file, setFile] = React.useState(null);
  const [validFrom, setValidFrom] = React.useState(existing?.valid_from || "");
  const [validUntil, setValidUntil] = React.useState(existing?.valid_until || "");
  const [docNumber, setDocNumber] = React.useState(existing?.document_number || "");
  const [labelOverride, setLabelOverride] = React.useState(existing?.label_override || "");
  const [notes, setNotes] = React.useState(existing?.notes || "");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const isTradeCert = docType?.key === "trade_certificate";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) {
      setError("Please choose a file to upload.");
      return;
    }
    if (!validUntil) {
      setError("Expiry date is required.");
      return;
    }
    if (validFrom && validUntil && validFrom > validUntil) {
      setError("‘Valid from’ must be on or before ‘Expires on’.");
      return;
    }
    setSubmitting(true);
    try {
      const dataUri = await fileToBase64(file);
      await apiClient.post("/driver/documents", {
        doc_type: docType.key,
        file_data: dataUri,
        file_mime: file.type,
        file_name: file.name,
        valid_from: validFrom || null,
        valid_until: validUntil || null,
        document_number: docNumber.trim() || null,
        label_override: isTradeCert ? (labelOverride.trim() || null) : null,
        notes: notes.trim() || null,
      });
      await onUploaded();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="upload-modal"
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-lg w-full p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
          {existing ? "Replace document" : "Upload document"}
        </p>
        <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">
          {docType.label}
        </h3>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="upload-form">
          {isTradeCert && (
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">
                Certificate type
              </label>
              <input
                type="text"
                value={labelOverride}
                onChange={(e) => setLabelOverride(e.target.value)}
                placeholder="e.g. CSCS card, WAMITAB, ADR"
                data-testid="upload-label-override"
                className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">
              File (PDF, JPG, PNG, HEIC — max 5MB)
            </label>
            <input
              type="file"
              accept={ACCEPTED_INPUT}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="upload-file-input"
              className="w-full text-sm file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#1E4D55] file:text-white file:font-semibold file:cursor-pointer hover:file:bg-[#163C42]"
            />
            {file && (
              <p className="mt-1.5 text-xs text-[#475569] inline-flex items-center gap-1.5">
                <ImageIcon size={12} />
                {file.name} · {formatBytes(file.size)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">
                Valid from
              </label>
              <input
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                data-testid="upload-valid-from"
                className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">
                Expires on <span className="text-[#B91C1C]">*</span>
              </label>
              <input
                type="date"
                required
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                data-testid="upload-valid-until"
                className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">
              Document number (optional)
            </label>
            <input
              type="text"
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              placeholder="e.g. licence or certificate number"
              data-testid="upload-doc-number"
              className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Anything the admin should know"
              data-testid="upload-notes"
              className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
            />
          </div>

          {error && (
            <div data-testid="upload-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="upload-cancel"
              className="px-5 py-3 text-sm font-semibold text-[#475569] hover:text-[#0A0A0A] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="upload-submit"
              className="inline-flex items-center gap-2 bg-[#1E4D55] text-white hover:bg-[#163C42] transition-colors px-5 py-3 font-semibold disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Uploading…</span>
                </>
              ) : (
                <span>Submit for review</span>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
